import { useState, useRef, useEffect } from "react";
import { Input, Typography, Flex, Alert } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import { BTN_TXT, DESC_TXT } from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  sendOtp,
  validateOtp,
  clearAuthError,
} from "../../redux/slices/AuthSlice";

const { Title, Text } = Typography;

const numberOfDigits = 6;

const OtpVerification = ({
  contactType,
  contactValue,
  setOtpModalOpen,
  setIsVerified,
  onVerifySuccess, 
}) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(new Array(numberOfDigits).fill(""));
  const [seconds, setSeconds] = useState(60);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const otpBoxReference = useRef([]);
  const isOtpFilled = otp.every((digit) => digit !== "");
  const { otpError, validateLoading, otpData } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (seconds > 0) {
      const timerId = setTimeout(() => setSeconds(seconds - 1), 1000);
      return () => clearTimeout(timerId);
    } else {
      setIsResendEnabled(true);
    }
  }, [seconds]);

  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().slice(0, numberOfDigits);
    if (!pastedData) return;

    const digits = pastedData.split("");
    const newArr = [...otp];

    digits.forEach((digit, i) => {
      if (i < numberOfDigits) {
        newArr[i] = digit;
      }
    });

    setOtp(newArr);

    const nextFocusIndex = Math.min(digits.length, numberOfDigits - 1);
    otpBoxReference.current[nextFocusIndex]?.focus();
  };

  const handleChange = (value, index) => {
    if (value.length > 1) {
      const digits = value.trim().slice(0, numberOfDigits).split("");
      const newArr = [...otp];
      digits.forEach((digit, i) => {
        if (i < numberOfDigits) {
          newArr[i] = digit;
        }
      });
      setOtp(newArr);
      const nextFocusIndex = Math.min(digits.length, numberOfDigits - 1);
      otpBoxReference.current[nextFocusIndex]?.focus();
      return;
    }

    let newArr = [...otp];
    newArr[index] = value;
    setOtp(newArr);

    if (value && index < numberOfDigits - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpBoxReference.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      if (isOtpFilled) {
        handleVerifyOtp();
      } else if (otp[index] && index < numberOfDigits - 1) {
        otpBoxReference.current[index + 1]?.focus();
      }
    }
  };

  const handleResendOtp = async () => {
    const payload = {
      email: contactValue,
      mobile: contactValue,
    };
    if (contactType === "email") {
      delete payload.mobile;
    } else {
      delete payload.email;
    }
    await dispatch(sendOtp(payload));
    setOtp(new Array(numberOfDigits).fill(""));
    setSeconds(60);
    setIsResendEnabled(false);
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join("");

    const payload = {
      email: contactValue,
      mobile: contactValue,
      otp: otpString,
      reqId: otpData?.reqId,
    };

    if (contactType === "email") {
      delete payload.mobile;
    } else {
      delete payload.email;
    }

    const response = await dispatch(validateOtp(payload));

    if (response.payload.statusCode === 201) {
      setOtp(new Array(numberOfDigits).fill(""));
      setIsVerified((prev) => ({
        ...prev,
        [contactType]: true,
      }));
      onVerifySuccess?.();
      setOtpModalOpen(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <Title level={3}>Verify OTP</Title>
      <Text style={{ fontSize: 14, textAlign: "center" }}>
        {`Enter the 6-digit OTP sent to ${contactValue}`}
      </Text>
      <Flex
        align="center"
        justify="space-between"
        style={{ marginTop: 20, width: "100%" }}
      >
        {otp.map((digit, index) => (
          <Input
            key={index}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={(reference) => (otpBoxReference.current[index] = reference)}
            style={{ textAlign: "center", width: 50.6, marginBottom: 20 }}
          />
        ))}
      </Flex>

      {otpError && (
        <Alert
          style={{ marginTop: 20 }}
          message={otpError?.message || otpError}
          type="error"
          showIcon
        />
      )}

      <PrimaryButton
        // type="primary"
        block
        onClick={handleVerifyOtp}
        disabled={!isOtpFilled}
        loading={validateLoading}
        style={{ marginTop: 20 }}
      >
        {isOtpFilled ? BTN_TXT.VERIFY_OTP : "Enter OTP to continue"}
      </PrimaryButton>

      <div style={{ marginTop: 20, textAlign: "center" }}>
        {!isResendEnabled ? (
          <Text>
            Resend OTP in {seconds} {seconds === 1 ? "second" : "seconds"}
          </Text>
        ) : (
          <Flex justify="center" align="center" gap={4}>
            <Text>Didn’t receive the OTP?</Text>
            <CustomButton
              type="link"
              onClick={handleResendOtp}
              style={{ padding: 0, height: "auto" }}
            >
              {BTN_TXT.RESEND_OTP}
            </CustomButton>
          </Flex>
        )}
      </div>

      {contactType === "email" && (
        <Text
          style={{
            color: "#40566D",
            marginTop: 20,
            fontSize: 12,
            textAlign: "center",
            display: "block",
          }}
        >
          {DESC_TXT.OTP_INFO_TEXT}
        </Text>
      )}
    </div>
  );
};

export default OtpVerification;
